import React, { useState, useEffect } from 'react';

const LayoutTab = ({ block, updateConfig, pages = [], selectedElementPath }) => {
  const [expandedColumn, setExpandedColumn] = useState(null);
  
  // Check if block is header or footer
  const isHeaderFooter = block.type === 'element' && (block.elementType === 'header' || block.elementType === 'footer');
  
  // Always use column layout - initialize if not present
  const layout = block.config?.layout || {};
  const hasColumnLayout = layout.type === 'columns';

  // Handle deep selection to auto-expand appropriate column
  useEffect(() => {
    if (selectedElementPath && selectedElementPath.includes('.')) {
      // Parse ID: blockId.columnId.elementId
      const parts = selectedElementPath.split('.');
      if (parts.length >= 2) {
        const columnId = parts[1];
        // Only expand if found in layout
        if (layout.columns?.some(c => c.id === columnId)) {
          setExpandedColumn(columnId);
            
          // If we have an element ID, scroll to it after a slight delay (to allow expansion render)
          if (parts.length >= 3) {
            const elementId = parts[2];
            setTimeout(() => {
              const elementDiv = document.getElementById(`element-control-${elementId}`);
              if (elementDiv) {
                    elementDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    // Add a temporary highlight flash
                    elementDiv.style.transition = 'box-shadow 0.3s';
                    elementDiv.style.boxShadow = '0 0 0 2px #3b82f6';
                    setTimeout(() => {
                        elementDiv.style.boxShadow = 'none';
                    }, 1500);
              }
            }, 100);
          }
        }
      }
    }
  }, [selectedElementPath, layout.columns]);

  // Initialize freeform layout and height if not present
  useEffect(() => {
    if (isHeaderFooter) {
      if (!layout.type) {
        updateConfig('layout', {
          type: 'freeform',
          pageRange: { startPage: 1, endPage: 'last' }
        });
      }
      
      // Ensure height is initialized to at least 100px for headers/footers
      if (!block.size?.height) {
        updateConfig('size', {
          ...block.size,
          height: 100
        });
      }
    }
  }, [isHeaderFooter, layout.type, block.size, updateConfig]);

  // Early returns AFTER all hooks
  if (!isHeaderFooter) return null;
  if (!layout.type) return <div style={{ padding: '12px' }}>Initializing layout...</div>;

  // Update layout property
  const updateLayout = (key, value) => {
    updateConfig('layout', {
      ...layout,
      [key]: value
    });
  };

  // Skip structured layout controls if freeform
  if (layout.type === 'freeform') {
    return (
      <div className="layout-tab" style={{ padding: '12px' }}>
        {/* Page Range Configuration */}
        <div className="property-group" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
          <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>Page Range</h4>
          <p style={{ fontSize: '12px', color: '#3b82f6', marginBottom: '12px' }}>Specify which pages this {block.elementType} should appear on</p>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#1e40af' }}>Start Page</label>
              <select
                value={layout.pageRange?.startPage || 1}
                onChange={(e) => updateLayout('pageRange', { ...layout.pageRange, startPage: parseInt(e.target.value) })}
                style={{ width: '100%', padding: '6px 8px', border: '1px solid #93c5fd', borderRadius: '4px', fontSize: '13px' }}
              >
                <option value={1}>Page 1 (First)</option>
                {pages.map((_, index) => {
                  if (index === 0) return null;
                  return <option key={index + 1} value={index + 1}>Page {index + 1}</option>;
                })}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#1e40af' }}>End Page</label>
              <select
                value={layout.pageRange?.endPage || 'last'}
                onChange={(e) => updateLayout('pageRange', { ...layout.pageRange, endPage: e.target.value })}
                style={{ width: '100%', padding: '6px 8px', border: '1px solid #93c5fd', borderRadius: '4px', fontSize: '13px' }}
              >
                <option value="last">Last Page</option>
                <option value="last-1">Second to Last</option>
                <option value="last-2">Third to Last</option>
                {pages.map((_, index) => (
                  <option key={index + 1} value={index + 1}>Page {index + 1}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '11px', color: '#1e40af' }}>
            <strong>Preview:</strong> {block.elementType.charAt(0).toUpperCase() + block.elementType.slice(1)} will appear from{' '}
            <strong>Page {layout.pageRange?.startPage || 1}</strong> to{' '}
            <strong>
              {layout.pageRange?.endPage === 'last' ? 'Last Page' :
               layout.pageRange?.endPage === 'last-1' ? 'Second to Last Page' :
               layout.pageRange?.endPage === 'last-2' ? 'Third to Last Page' :
               `Page ${layout.pageRange?.endPage || 'Last'}`}
            </strong>
          </div>
        </div>

        {/* <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>🎨</div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Freeform Layout Active</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            You can drag and drop any widget into the {block.elementType} area.
          </div>
        </div> */}
      </div>
    );
  }

  // Add new column
  const handleAddColumn = () => {
    const newColumn = {
      id: `col-${Date.now()}`,
      flex: 1,
      width: 'auto',
      style: {
        backgroundColor: 'transparent',
        padding: 8,
        margin: 0,
        borderWidth: 0,
        borderColor: '#000000',
        borderRadius: 0,
        direction: 'column',
        justify: 'flex-start',
        align: 'flex-start',
        gap: 4
      },
      elements: [
        {
          id: `elem-${Date.now()}`,
          type: 'text',
          content: 'New Column',
          style: { fontSize: '14px', fontWeight: 'normal', textColor: 'inherit' }
        }
      ]
    };

    updateConfig('layout', {
      ...layout,
      columns: [...(layout.columns || []), newColumn]
    });
  };

  // Remove column
  const handleRemoveColumn = (columnId) => {
    updateConfig('layout', {
      ...layout,
      columns: layout.columns.filter(col => col.id !== columnId)
    });
  };

  // Update column property
  const updateColumn = (columnId, key, value) => {
    const updatedColumns = layout.columns.map(col => {
      if (col.id === columnId) {
        if (key.startsWith('style.')) {
          const styleKey = key.replace('style.', '');
          return {
            ...col,
            style: {
              ...col.style,
              [styleKey]: value
            }
          };
        }
        return { ...col, [key]: value };
      }
      return col;
    });

    updateConfig('layout', {
      ...layout,
      columns: updatedColumns
    });
  };

  // Add element to column
  const handleAddElement = (columnId, elementType) => {
    const newElement = {
      id: `elem-${Date.now()}`,
      type: elementType,
      content: elementType === 'text' ? 'New Text' : elementType === 'image' ? '' : '★',
      style: {
        fontSize: elementType === 'icon' ? '24px' : '14px',
        fontWeight: 'normal',
        textColor: 'inherit',
        textAlign: 'left'
      }
    };

    const updatedColumns = layout.columns.map(col => {
      if (col.id === columnId) {
        return {
          ...col,
          elements: [...(col.elements || []), newElement]
        };
      }
      return col;
    });

    updateConfig('layout', {
      ...layout,
      columns: updatedColumns
    });
  };

  // Remove element from column
  const handleRemoveElement = (columnId, elementId) => {
    const updatedColumns = layout.columns.map(col => {
      if (col.id === columnId) {
        return {
          ...col,
          elements: col.elements.filter(el => el.id !== elementId)
        };
      }
      return col;
    });

    updateConfig('layout', {
      ...layout,
      columns: updatedColumns
    });
  };

  // Update element property
  const updateElement = (columnId, elementId, key, value) => {
    const updatedColumns = layout.columns.map(col => {
      if (col.id === columnId) {
        const updatedElements = col.elements.map(el => {
          if (el.id === elementId) {
            if (key.startsWith('style.')) {
              const styleKey = key.replace('style.', '');
              return {
                ...el,
                style: {
                  ...el.style,
                  [styleKey]: value
                }
              };
            }
            return { ...el, [key]: value };
          }
          return el;
        });
        return { ...col, elements: updatedElements };
      }
      return col;
    });

    updateConfig('layout', {
      ...layout,
      columns: updatedColumns
    });
  };

  return (
    <div className="layout-tab" style={{ padding: '12px' }}>
      {/* Container Layout Configuration */}
      <div className="property-group" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
        <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#111827' }}>Container Layout</h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#374151' }}>Direction</label>
            <select
              value={layout.direction || 'row'}
              onChange={(e) => updateLayout('direction', e.target.value)}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
            >
              <option value="row">Row →</option>
              <option value="column">Column ↓</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#374151' }}>Gap (px)</label>
            <input
              type="number"
              value={layout.gap || 0}
              onChange={(e) => updateLayout('gap', parseInt(e.target.value) || 0)}
              min="0"
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#374151' }}>Justify Content</label>
            <select
              value={layout.justify || 'flex-start'}
              onChange={(e) => updateLayout('justify', e.target.value)}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
            >
              <option value="flex-start">Start</option>
              <option value="center">Center</option>
              <option value="flex-end">End</option>
              <option value="space-between">Space Between</option>
              <option value="space-around">Space Around</option>
              <option value="space-evenly">Space Evenly</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#374151' }}>Align Items</label>
            <select
              value={layout.align || 'stretch'}
              onChange={(e) => updateLayout('align', e.target.value)}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
            >
              <option value="flex-start">Start</option>
              <option value="center">Center</option>
              <option value="flex-end">End</option>
              <option value="stretch">Stretch</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#374151' }}>Padding (px)</label>
            <input
              type="number"
              value={layout.padding !== undefined ? layout.padding : 8}
              onChange={(e) => updateLayout('padding', parseInt(e.target.value) || 0)}
              min="0"
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#374151' }}>Margin (px)</label>
            <input
              type="number"
              value={layout.margin !== undefined ? layout.margin : 0}
              onChange={(e) => updateLayout('margin', parseInt(e.target.value) || 0)}
              min="0"
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
            />
          </div>
        </div>
      </div>

      {/* Page Range Configuration */}
      <div className="property-group" style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
        <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>Page Range</h4>
        <p style={{ fontSize: '12px', color: '#3b82f6', marginBottom: '12px' }}>Specify which pages this {block.elementType} should appear on</p>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#1e40af' }}>Start Page</label>
            <select
              value={layout.pageRange?.startPage || 1}
              onChange={(e) => updateLayout('pageRange', { ...layout.pageRange, startPage: parseInt(e.target.value) })}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #93c5fd', borderRadius: '4px', fontSize: '13px' }}
            >
              <option value={1}>Page 1 (First)</option>
              {pages.map((_, index) => {
                if (index === 0) return null;
                return <option key={index + 1} value={index + 1}>Page {index + 1}</option>;
              })}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#1e40af' }}>End Page</label>
            <select
              value={layout.pageRange?.endPage || 'last'}
              onChange={(e) => updateLayout('pageRange', { ...layout.pageRange, endPage: e.target.value })}
              style={{ width: '100%', padding: '6px 8px', border: '1px solid #93c5fd', borderRadius: '4px', fontSize: '13px' }}
            >
              <option value="last">Last Page</option>
              <option value="last-1">Second to Last</option>
              <option value="last-2">Third to Last</option>
              {pages.map((_, index) => (
                <option key={index + 1} value={index + 1}>Page {index + 1}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#dbeafe', borderRadius: '4px', fontSize: '11px', color: '#1e40af' }}>
          <strong>Preview:</strong> {block.elementType.charAt(0).toUpperCase() + block.elementType.slice(1)} will appear from{' '}
          <strong>Page {layout.pageRange?.startPage || 1}</strong> to{' '}
          <strong>
            {layout.pageRange?.endPage === 'last' ? 'Last Page' :
             layout.pageRange?.endPage === 'last-1' ? 'Second to Last Page' :
             layout.pageRange?.endPage === 'last-2' ? 'Third to Last Page' :
             `Page ${layout.pageRange?.endPage || 'Last'}`}
          </strong>
        </div>
      </div>

      {/* Columns */}
      <div className="property-group">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>Columns ({layout.columns?.length || 0})</h4>
          <button
            onClick={handleAddColumn}
            disabled={layout.columns?.length >= 4}
            style={{
              padding: '6px 12px',
              backgroundColor: layout.columns?.length >= 4 ? '#e5e7eb' : '#3b82f6',
              color: layout.columns?.length >= 4 ? '#9ca3af' : '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: layout.columns?.length >= 4 ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              fontWeight: '500'
            }}
          >
            + Add Column
          </button>
        </div>

        {layout.columns && layout.columns.map((column, index) => (
          <div key={column.id} style={{ marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            {/* Column Header */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '10px 12px', 
              backgroundColor: '#f3f4f6',
              borderBottom: expandedColumn === column.id ? '1px solid #e5e7eb' : 'none'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => setExpandedColumn(expandedColumn === column.id ? null : column.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    fontSize: '12px',
                    color: '#6b7280'
                  }}
                >
                  {expandedColumn === column.id ? '▼' : '▶'}
                </button>
                <strong style={{ fontSize: '13px', color: '#111827' }}>Column {index + 1}</strong>
              </div>
              <button
                onClick={() => handleRemoveColumn(column.id)}
                disabled={layout.columns.length <= 1}
                style={{
                  padding: '4px 8px',
                  backgroundColor: layout.columns.length <= 1 ? '#fee2e2' : '#ef4444',
                  color: layout.columns.length <= 1 ? '#fca5a5' : '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: layout.columns.length <= 1 ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                Remove
              </button>
            </div>

            {/* Column Content */}
            {expandedColumn === column.id && (
              <div style={{ padding: '12px' }}>
                {/* Column Properties */}
                <div style={{ marginBottom: '16px' }}>
                  <h5 style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>Column Properties</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: '#6b7280' }}>Flex Grow</label>
                      <input
                        type="number"
                        value={column.flex || 1}
                        onChange={(e) => updateColumn(column.id, 'flex', parseInt(e.target.value) || 1)}
                        min="0"
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: '#6b7280' }}>Width</label>
                      <input
                        type="text"
                        value={column.width || 'auto'}
                        onChange={(e) => updateColumn(column.id, 'width', e.target.value)}
                        placeholder="auto, 100px, 50%"
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Column Element Layout */}
                <div style={{ marginBottom: '16px', padding: '10px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fde047' }}>
                  <h5 style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#92400e' }}>Element Layout (within column)</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: '#78350f' }}>Direction</label>
                      <select
                        value={column.style?.direction || 'column'}
                        onChange={(e) => updateColumn(column.id, 'style.direction', e.target.value)}
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #fbbf24', borderRadius: '4px', fontSize: '12px' }}
                      >
                        <option value="row">Row →</option>
                        <option value="column">Column ↓</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: '#78350f' }}>Gap (px)</label>
                      <input
                        type="number"
                        value={column.style?.gap || 0}
                        onChange={(e) => updateColumn(column.id, 'style.gap', parseInt(e.target.value) || 0)}
                        min="0"
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #fbbf24', borderRadius: '4px', fontSize: '12px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: '#78350f' }}>Justify</label>
                      <select
                        value={column.style?.justify || 'flex-start'}
                        onChange={(e) => updateColumn(column.id, 'style.justify', e.target.value)}
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #fbbf24', borderRadius: '4px', fontSize: '12px' }}
                      >
                        <option value="flex-start">Start</option>
                        <option value="center">Center</option>
                        <option value="flex-end">End</option>
                        <option value="space-between">Space Between</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: '#78350f' }}>Align</label>
                      <select
                        value={column.style?.align || 'flex-start'}
                        onChange={(e) => updateColumn(column.id, 'style.align', e.target.value)}
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #fbbf24', borderRadius: '4px', fontSize: '12px' }}
                      >
                        <option value="flex-start">Start</option>
                        <option value="center">Center</option>
                        <option value="flex-end">End</option>
                        <option value="stretch">Stretch</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Column Styling */}
                <div style={{ marginBottom: '16px' }}>
                  <h5 style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>Column Styling</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: '#6b7280' }}>Background</label>
                      <input
                        type="color"
                        value={column.style?.backgroundColor || '#ffffff'}
                        onChange={(e) => updateColumn(column.id, 'style.backgroundColor', e.target.value)}
                        style={{ width: '100%', height: '32px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: '#6b7280' }}>Padding (px)</label>
                      <input
                        type="number"
                        value={column.style?.padding || 0}
                        onChange={(e) => updateColumn(column.id, 'style.padding', parseInt(e.target.value) || 0)}
                        min="0"
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: '#6b7280' }}>Border Width (px)</label>
                      <input
                        type="number"
                        value={column.style?.borderWidth || 0}
                        onChange={(e) => updateColumn(column.id, 'style.borderWidth', parseInt(e.target.value) || 0)}
                        min="0"
                        style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px' }}
                      />
                    </div>
                    {column.style?.borderWidth > 0 && (
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: '#6b7280' }}>Border Color</label>
                        <input
                          type="color"
                          value={column.style?.borderColor || '#000000'}
                          onChange={(e) => updateColumn(column.id, 'style.borderColor', e.target.value)}
                          style={{ width: '100%', height: '32px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Elements */}
                <div style={{ paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h5 style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Elements ({column.elements?.length || 0})</h5>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAddElement(column.id, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      defaultValue=""
                      style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', backgroundColor: '#10b981', color: '#ffffff', fontWeight: '500' }}
                    >
                      <option value="">+ Add Element</option>
                      <option value="text">Text</option>
                      <option value="image">Image</option>
                      <option value="icon">Icon</option>
                    </select>
                  </div>

                  {column.elements && column.elements.map((element, elemIndex) => (
                    <div 
                      key={element.id} 
                      id={`element-control-${element.id}`}
                      style={{ marginBottom: '8px', padding: '8px', backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <strong style={{ fontSize: '11px', color: '#374151' }}>
                          {element.type === 'text' ? '📝' : element.type === 'image' ? '🖼️' : '⭐'} {element.type.charAt(0).toUpperCase() + element.type.slice(1)}
                        </strong>
                        <button
                          onClick={() => handleRemoveElement(column.id, element.id)}
                          style={{
                            padding: '2px 6px',
                            backgroundColor: '#ef4444',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '10px'
                          }}
                        >
                          Remove
                        </button>
                      </div>

                      <div style={{ marginBottom: '6px' }}>
                        <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', color: '#6b7280' }}>Content</label>
                        {element.type === 'image' ? (
                          <input
                            type="text"
                            value={element.content || ''}
                            onChange={(e) => updateElement(column.id, element.id, 'content', e.target.value)}
                            placeholder="URL or {{ MSP.logo }} / {{ MSP.large_logo }}"
                            style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                          />
                        ) : (
                          <>
                            <textarea
                              id={`content-${column.id}-${element.id}`}
                              value={element.content || ''}
                              onChange={(e) => updateElement(column.id, element.id, 'content', e.target.value)}
                              rows="2"
                              style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', resize: 'vertical' }}
                            />
                            {element.type === 'text' && (
                              <div style={{ marginTop: '4px' }}>
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const textarea = document.getElementById(`content-${column.id}-${element.id}`);
                                      const cursorPos = textarea?.selectionStart || element.content?.length || 0;
                                      const currentContent = element.content || '';
                                      const newContent = currentContent.slice(0, cursorPos) + e.target.value + currentContent.slice(cursorPos);
                                      updateElement(column.id, element.id, 'content', newContent);
                                      e.target.value = '';
                                      // Refocus textarea after insertion
                                      setTimeout(() => {
                                        if (textarea) {
                                          textarea.focus();
                                          textarea.setSelectionRange(cursorPos + e.target.value.length, cursorPos + e.target.value.length);
                                        }
                                      }, 10);
                                    }
                                  }}
                                  defaultValue=""
                                  style={{ 
                                    width: '100%', 
                                    padding: '4px 6px', 
                                    border: '1px solid #3b82f6', 
                                    borderRadius: '4px', 
                                    fontSize: '10px', 
                                    backgroundColor: '#eff6ff', 
                                    color: '#1e40af',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <option value="">+ Insert Variable</option>
                                  <optgroup label="📅 Date Variables">
                                    <option value="{{ date_today }}">Today's Date</option>
                                    <option value="{{ date_today | short }}">Today's Date (Short)</option>
                                    <option value="{{ date_today | medium }}">Today's Date (Medium)</option>
                                    <option value="{{ date_today | long }}">Today's Date (Long)</option>
                                    <option value="{{ date_of_scan }}">Scan Date</option>
                                    <option value="{{ date_of_scan | short }}">Scan Date (Short)</option>
                                    <option value="{{ date_of_scan | medium }}">Scan Date (Medium)</option>
                                    <option value="{{ date_of_scan | long }}">Scan Date (Long)</option>
                                  </optgroup>
                                  <optgroup label="🏢 Organization">
                                    <option value="{{ organization_name }}">Organization Name</option>
                                  </optgroup>
                                  <optgroup label="👤 MSP Information">
                                    <option value="{{ tenant_name }}">Tenant Name</option>
                                    <option value="{{ MSP.user.first_name }}">MSP First Name</option>
                                    <option value="{{ MSP.user.last_name }}">MSP Last Name</option>
                                    <option value="{{ MSP.logo }}">MSP Logo</option>
                                    <option value="{{ MSP.large_logo }}">MSP Large Logo</option>
                                  </optgroup>
                                  <optgroup label="🏪 Client Information">
                                    <option value="{{ client.name }}">Client Name</option>
                                    <option value="{{ client.email }}">Client Email</option>
                                    <option value="{{ client.phone_number }}">Client Phone</option>
                                    <option value="{{ client.domain }}">Client Domain</option>
                                  </optgroup>
                                </select>
                                <div style={{ 
                                  marginTop: '4px', 
                                  padding: '4px 6px', 
                                  backgroundColor: '#fef3c7', 
                                  borderRadius: '4px', 
                                  fontSize: '9px', 
                                  color: '#92400e',
                                  lineHeight: '1.3'
                                }}>
                                  💡 Variables will be replaced with actual data when generating reports
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {element.type === 'image' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', color: '#6b7280' }}>Width (px)</label>
                            <input
                              type="text"
                              value={(element.style?.width || '').replace(/px$/, '')}
                              onChange={(e) => {
                                const val = e.target.value;
                                const newVal = /^\d+$/.test(val) ? `${val}px` : val;
                                updateElement(column.id, element.id, 'style.width', newVal);
                              }}
                              placeholder="auto"
                              style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', color: '#6b7280' }}>Height (px)</label>
                            <input
                              type="text"
                              value={(element.style?.height || '').replace(/px$/, '')}
                              onChange={(e) => {
                                const val = e.target.value;
                                const newVal = /^\d+$/.test(val) ? `${val}px` : val;
                                updateElement(column.id, element.id, 'style.height', newVal);
                              }}
                              placeholder="auto"
                              style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', color: '#6b7280' }}>Fit</label>
                            <select
                              value={element.style?.objectFit || 'contain'}
                              onChange={(e) => updateElement(column.id, element.id, 'style.objectFit', e.target.value)}
                              style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                            >
                              <option value="contain">Contain</option>
                              <option value="cover">Cover</option>
                              <option value="fill">Fill</option>
                              <option value="none">None</option>
                            </select>
                          </div>
                        </div>
                      )}
                      {element.type !== 'image' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', color: '#6b7280' }}>Size (px)</label>
                            <input
                              type="number"
                              min="8"
                              max="96"
                              value={parseInt(element.style?.fontSize) || 14}
                              onChange={(e) => updateElement(column.id, element.id, 'style.fontSize', `${e.target.value}px`)}
                              style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', color: '#6b7280' }}>Weight</label>
                            <select
                              value={element.style?.fontWeight === 'bold' ? '700' : element.style?.fontWeight === 'lighter' ? '300' : element.style?.fontWeight === 'normal' ? '400' : (element.style?.fontWeight || '400')}
                              onChange={(e) => updateElement(column.id, element.id, 'style.fontWeight', e.target.value)}
                              style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px' }}
                            >
                              <option value="300">Light</option>
                              <option value="400">Normal</option>
                              <option value="700">Bold</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', color: '#6b7280' }}>Color</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', height: '26px' }}>
                              <input 
                                type="color" 
                                value={element.style?.textColor || '#000000'}
                                onChange={(e) => updateElement(column.id, element.id, 'style.textColor', e.target.value)}
                                style={{ width: '24px', height: '24px', border: '1px solid #d1d5db', borderRadius: '4px', padding: 0, cursor: 'pointer', flexShrink: 0 }}
                              />
                              {element.style?.textColor && (
                                <button
                                  onClick={() => updateElement(column.id, element.id, 'style.textColor', undefined)}
                                  title="Reset to inherit"
                                  style={{ fontSize: '10px', padding: '0', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LayoutTab;
